'use client';

import { useEffect } from 'react';

interface RedirectHandlerProps {
    slug: string;
    targetUrl: string;
    trustedDomains: string[];
    referralName: string;
}

interface ReferralEvent {
    event: 'referral_redirect';
    redirect_slug: string;
    referral_name: string;
    target_url: string;
}

const RedirectHandler: React.FC<RedirectHandlerProps> = ({
    slug,
    targetUrl,
    trustedDomains,
    referralName,
}) => {
    const isValid = isValidUrl(targetUrl, trustedDomains);
    const eventData: ReferralEvent = {
        event: 'referral_redirect',
        redirect_slug: slug,
        referral_name: referralName,
        target_url: targetUrl,
    };

    // The inline script redirects on a hard load as soon as the HTML is parsed (before hydration).
    // useEffect covers client-side navigations, where inline scripts don't run.
    useEffect(() => {
        if (!isValid) {
            console.error('Invalid or untrusted URL:', targetUrl);
            return;
        }
        redirect(eventData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid, slug, targetUrl, referralName]);

    return (
        <>
            <div className='fixed inset-x-0 top-0 z-50 h-[3px] overflow-hidden'>
                <div className='h-full bg-primary animate-[redirect-progress_1.2s_ease-out_forwards]' />
            </div>
            <style>{`@keyframes redirect-progress{from{width:0}to{width:90%}}`}</style>
            {isValid && (
                <script
                    dangerouslySetInnerHTML={{
                        __html: `(${INLINE_REDIRECT})(${JSON.stringify(eventData).replace(/</g, '\\u003c')});`,
                    }}
                />
            )}
        </>
    );
};

// Same logic as redirect() below, as a plain string so server and client markup match.
const INLINE_REDIRECT =
    'function(e){var w=window;if(w.__khsRedirected)return;w.__khsRedirected=true;' +
    '(w.dataLayer=w.dataLayer||[]).push(e);w.location.replace(e.target_url);}';

function redirect(eventData: ReferralEvent) {
    const w = window as Window & { __khsRedirected?: boolean; dataLayer?: unknown[] };
    if (w.__khsRedirected) return;
    w.__khsRedirected = true;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(eventData);
    w.location.replace(eventData.target_url);
}

// Helper function to validate URLs against trusted domains
const isValidUrl = (url: string, trustedDomains: string[]): boolean => {
    try {
        const parsedUrl = new URL(url);
        const isTrusted =
            trustedDomains.length === 0 || trustedDomains.includes(parsedUrl.hostname);
        return parsedUrl.protocol === 'https:' && isTrusted;
    } catch {
        return false;
    }
};

export default RedirectHandler;
